/**
 * Pack Analysis Workbench — Simulator: canonical-key resolution for a picked
 * item-master row.
 *
 * Spec `pack-analysis-simulator-item-aware-line-entry` (Phase A, task A.2).
 *
 * When the operator picks an item (in the per-line combobox or the Item Master
 * Picker), the cartonization engine matches patterns/observations on the
 * ML2-*composed* canonical key — stored on the item as a `CANONICAL_ITEMKEY`
 * alternate key (lock #52). We prefer that value at the strictest scope; if the
 * item has no such alt-key yet, we fall back to `canonicalIdentifier` (the
 * backend `IItemReadRepo.GetByItemKeyAsync` does the same fallback). Mirroring
 * the backend's strictest-wins scope order keeps FE and BE resolution aligned.
 *
 * Pure functions — no Redux, no fetch.
 */
import type { Item, ItemAlternateKey } from '@/app/services/api/itemMasterService';
import type { ResolvedItemRef } from './lineModel';

/** The alt-key `identifier_type` that carries the ML2-composed canonical key. */
export const CANONICAL_ITEMKEY_TYPE = 'CANONICAL_ITEMKEY';

/** Strictest-wins scope ranking — lower = stricter (preferred). Matches the
 *  backend alt-key resolution chain `buyer_supplier → buyer → supplier → tenant
 *  → platform`. Unknown/blank scopes rank last. Case-insensitive. */
export function scopeRank(scope: string | null | undefined): number {
  switch ((scope ?? '').trim().toLowerCase()) {
    case 'buyer_supplier':
      return 0;
    case 'buyer':
      return 1;
    case 'supplier':
      return 2;
    case 'tenant':
      return 3;
    case 'platform':
      return 4;
    default:
      return 99;
  }
}

function isCanonicalItemKey(k: ItemAlternateKey): boolean {
  return (k.identifierType ?? '').trim().toUpperCase() === CANONICAL_ITEMKEY_TYPE && !!(k.identifierValue ?? '').trim();
}

/**
 * The canonical key to send to `/cartonization` for this item:
 *  1. the strictest-scope active `CANONICAL_ITEMKEY` alt-key value, else
 *  2. `item.canonicalIdentifier`, else
 *  3. `null` (caller flags the line and excludes it — Req 5.3).
 *
 * Deterministic: among equal-scope `CANONICAL_ITEMKEY` values, the one seen
 * first wins (stable — Property P4).
 */
export function resolveCanonicalKey(item: Pick<Item, 'canonicalIdentifier' | 'alternateKeys'>): string | null {
  const keys = (item.alternateKeys ?? []).filter(isCanonicalItemKey);
  if (keys.length > 0) {
    let best = keys[0];
    let bestRank = scopeRank(best.scope);
    for (let i = 1; i < keys.length; i += 1) {
      const r = scopeRank(keys[i].scope);
      if (r < bestRank) {
        best = keys[i];
        bestRank = r;
      }
    }
    const v = (best.identifierValue ?? '').trim();
    if (v) return v;
  }
  const canon = (item.canonicalIdentifier ?? '').trim();
  return canon || null;
}

/** Build a `ResolvedItemRef` for an editor line, or `null` if no canonical key
 *  can be determined for the item. */
export function toResolvedItemRef(item: Item): ResolvedItemRef | null {
  const key = resolveCanonicalKey(item);
  if (!key) return null;
  const itemType: 'SKU' | 'PACK' = (item.itemType ?? 'SKU').toUpperCase() === 'PACK' ? 'PACK' : 'SKU';
  return {
    itemId: item.id,
    canonicalIdentifier: item.canonicalIdentifier,
    itemType,
    description: item.description ?? null,
    resolvedCanonicalKey: key,
  };
}
